import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, subDays } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  FileText, ArrowLeft, RefreshCw, Download, FileSpreadsheet, 
  BarChart3, PieChartIcon, Activity, TrendingUp, Clock, Save,
  Loader2, Filter, Calendar, Building, Shield, AlertTriangle,
  CheckCircle, XCircle, Users, FolderOpen, CalendarClock
} from 'lucide-react';
import ScheduledReportsContent from './ScheduledReportsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function ReportsPage() {
  const { getAuthHeaders, selectedStudy: contextStudy } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [reportTypes, setReportTypes] = useState([]);
  const [studies, setStudies] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  
  // Report generation state - Use context study as default
  const [selectedReportType, setSelectedReportType] = useState('tmf_completeness');
  const [reportParams, setReportParams] = useState({
    study_id: contextStudy?.study_id || 'all',
    regulation: 'all',
    date_from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd')
  });
  const [generatedReport, setGeneratedReport] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);

  // Set selected study from context when it changes
  useEffect(() => {
    if (contextStudy?.study_id) {
      setReportParams(prev => ({ ...prev, study_id: contextStudy.study_id }));
    }
  }, [contextStudy]);

  const fetchInitialData = useCallback(async () => {
    try {
      const { headers } = getAuthHeaders();

      // Fetch report types
      const typesRes = await axios.get(`${API_URL}/api/reports/types`, { headers });
      setReportTypes(typesRes.data?.report_types || []);

      // Fetch studies for filters
      const studiesRes = await axios.get(`${API_URL}/api/studies`, { headers });
      setStudies(studiesRes.data?.studies || []);

      // Fetch saved reports
      const savedRes = await axios.get(`${API_URL}/api/reports/saved?limit=20`, { headers });
      setSavedReports(savedRes.data?.reports || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const generateReport = async () => {
    setLoading(true);
    setGeneratedReport(null);
    
    try {
      const { headers } = getAuthHeaders();
      
      const params = { ...reportParams };
      if (params.study_id === 'all') delete params.study_id;
      if (params.regulation === 'all') delete params.regulation;
      
      let endpoint = '';
      switch (selectedReportType) {
        case 'tmf_completeness':
          endpoint = '/api/reports/tmf-completeness';
          break;
        case 'compliance_summary':
          endpoint = '/api/reports/compliance-summary';
          break;
        case 'document_activity':
          endpoint = '/api/reports/document-activity';
          break;
        case 'audit_trail':
          endpoint = '/api/reports/audit-trail';
          break;
        case 'study_health':
          endpoint = '/api/reports/study-health';
          break;
        default:
          throw new Error('Invalid report type');
      }
      
      const response = await axios.post(`${API_URL}${endpoint}`, params, { headers });
      setGeneratedReport(response.data);
      setShowReportDialog(true);
      toast.success('Report generated successfully');
      
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    if (!generatedReport) return;
    
    try {
      const { headers } = getAuthHeaders();
      
      const params = { ...reportParams };
      if (params.study_id === 'all') delete params.study_id;
      if (params.regulation === 'all') delete params.regulation;
      
      await axios.post(`${API_URL}/api/reports/save`, null, {
        headers,
        params: { report_type: selectedReportType, params: JSON.stringify(params) }
      });
      
      toast.success('Report saved');
      fetchInitialData();
      
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  const exportCSV = async () => {
    try {
      const { headers } = getAuthHeaders();
      
      const params = { ...reportParams };
      if (params.study_id === 'all') delete params.study_id;
      
      const response = await axios.post(
        `${API_URL}/api/reports/export/csv`,
        { report_type: selectedReportType, params },
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedReportType}_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported');
      
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const loadSavedReport = async (reportId) => {
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/reports/saved/${reportId}`, { headers });
      setGeneratedReport(response.data?.data);
      setShowReportDialog(true);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const renderReportContent = () => {
    if (!generatedReport) return null;

    const reportType = generatedReport.report_type;

    if (reportType === 'TMF Completeness Report') {
      return (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{generatedReport.summary?.total_studies || 0}</p>
                <p className="text-sm text-blue-700">Total Studies</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{generatedReport.summary?.inspection_ready || 0}</p>
                <p className="text-sm text-green-700">Inspection Ready</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{generatedReport.summary?.at_risk || 0}</p>
                <p className="text-sm text-amber-700">At Risk</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{generatedReport.summary?.average_completeness || 0}%</p>
                <p className="text-sm text-purple-700">Avg Completeness</p>
              </CardContent>
            </Card>
          </div>

          {/* Completeness Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Study Completeness</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={generatedReport.studies?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="study_name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="overall_completeness" fill="#3B82F6" name="Completeness %" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Study Details Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Study Details</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Study</th>
                      <th className="p-2 text-left">Sponsor</th>
                      <th className="p-2 text-center">Completeness</th>
                      <th className="p-2 text-center">Readiness</th>
                      <th className="p-2 text-center">Critical</th>
                      <th className="p-2 text-center">High</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedReport.studies?.map((study, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{study.study_name}</td>
                        <td className="p-2">{study.sponsor}</td>
                        <td className="p-2 text-center">
                          <Badge className={study.overall_completeness >= 80 ? 'bg-green-500' : study.overall_completeness >= 50 ? 'bg-amber-500' : 'bg-red-500'}>
                            {study.overall_completeness}%
                          </Badge>
                        </td>
                        <td className="p-2 text-center">
                          <Badge className={study.inspection_readiness === 'ready' ? 'bg-green-500' : study.inspection_readiness === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'}>
                            {study.inspection_readiness}
                          </Badge>
                        </td>
                        <td className="p-2 text-center">{study.alerts?.critical || 0}</td>
                        <td className="p-2 text-center">{study.alerts?.high || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (reportType === 'Compliance Summary Report') {
      return (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{generatedReport.summary?.total_assessments || 0}</p>
                <p className="text-sm text-blue-700">Total Assessments</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{generatedReport.summary?.overall_compliance_rate || 0}%</p>
                <p className="text-sm text-green-700">Compliance Rate</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{generatedReport.summary?.regulations_covered || 0}</p>
                <p className="text-sm text-purple-700">Regulations</p>
              </CardContent>
            </Card>
          </div>

          {/* Compliance by Regulation Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Compliance by Regulation</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={generatedReport.by_regulation || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="regulation_name" type="category" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="average_score" fill="#10B981" name="Avg Score" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Regulation Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Regulation Details</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {generatedReport.by_regulation?.map((reg, idx) => (
                  <div key={idx} className="p-3 border-b">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">{reg.regulation_name}</h4>
                      <Badge className={reg.average_score >= 80 ? 'bg-green-500' : reg.average_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}>
                        {reg.average_score}%
                      </Badge>
                    </div>
                    <div className="flex gap-4 text-sm text-slate-600">
                      <span>Assessments: {reg.total_assessments}</span>
                      <span className="text-green-600">Compliant: {reg.compliant}</span>
                      <span className="text-red-600">Non-Compliant: {reg.non_compliant}</span>
                    </div>
                    {reg.common_gaps?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500">Top gaps:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {reg.common_gaps.slice(0, 3).map((gap, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{gap.requirement_id}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (reportType === 'Document Activity Report') {
      const activityData = Object.entries(generatedReport.by_action || {}).map(([action, count]) => ({
        action: action.replace(/_/g, ' '),
        count
      }));

      const dailyData = Object.entries(generatedReport.daily_activity || {}).map(([date, count]) => ({
        date,
        count
      })).sort((a, b) => a.date.localeCompare(b.date));

      return (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{generatedReport.summary?.total_activities || 0}</p>
                <p className="text-sm text-blue-700">Total Activities</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{generatedReport.summary?.unique_users || 0}</p>
                <p className="text-sm text-green-700">Unique Users</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{generatedReport.summary?.action_types || 0}</p>
                <p className="text-sm text-purple-700">Action Types</p>
              </CardContent>
            </Card>
          </div>

          {/* Activity Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Daily Activity Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#3B82F680" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Activity by Type */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Activity by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={activityData.slice(0, 8)}
                      dataKey="count"
                      nameKey="action"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {activityData.slice(0, 8).map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top Users</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  {generatedReport.top_users?.slice(0, 10).map((user, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 border-b">
                      <span className="text-sm truncate">{user.user}</span>
                      <Badge>{user.total}</Badge>
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    // Default: show raw JSON
    return (
      <ScrollArea className="h-[500px]">
        <pre className="text-xs bg-slate-100 p-4 rounded overflow-auto">
          {JSON.stringify(generatedReport, null, 2)}
        </pre>
      </ScrollArea>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Advanced Reports</h1>
                  <p className="text-xs text-white/60">Generate & Export Reports</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchInitialData}
                className="border-white/20 text-white hover:bg-slate-50/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="generate" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-generate">
              <FileText className="w-4 h-4 mr-2" />
              Generate Report
            </TabsTrigger>
            <TabsTrigger value="saved" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-saved">
              <Save className="w-4 h-4 mr-2" />
              Saved Reports ({savedReports.length})
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-scheduled">
              <CalendarClock className="w-4 h-4 mr-2" />
              Scheduled Reports
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-6" data-testid="generate-content">
            <div className="grid grid-cols-4 gap-6">
              {/* Report Type Selection */}
              <div className="col-span-1">
                <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                      Report Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {reportTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setSelectedReportType(type.id)}
                        className={`p-3 rounded-lg cursor-pointer border transition-all ${
                          selectedReportType === type.id
                            ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-500'
                            : 'bg-white border-slate-200 hover:border-indigo-200'
                        }`}
                      >
                        <h4 className="font-medium text-slate-900 text-sm">{type.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Parameters & Preview */}
              <div className="col-span-3 space-y-6">
                {/* Parameters */}
                <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Filter className="w-5 h-5 text-indigo-600" />
                      Report Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-slate-700">Study</Label>
                        <Select value={reportParams.study_id} onValueChange={(v) => setReportParams(p => ({ ...p, study_id: v }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Studies" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Studies</SelectItem>
                            {studies.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedReportType === 'compliance_summary' && (
                        <div>
                          <Label className="text-slate-700">Regulation</Label>
                          <Select value={reportParams.regulation} onValueChange={(v) => setReportParams(p => ({ ...p, regulation: v }))}>
                            <SelectTrigger>
                              <SelectValue placeholder="All Regulations" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Regulations</SelectItem>
                              <SelectItem value="21_cfr_part_11">21 CFR Part 11</SelectItem>
                              <SelectItem value="ich_gcp">ICH-GCP</SelectItem>
                              <SelectItem value="eu_annex_11">EU Annex 11</SelectItem>
                              <SelectItem value="ema_gxp">EMA GxP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <Label className="text-slate-700">From Date</Label>
                        <Input
                          type="date"
                          value={reportParams.date_from}
                          onChange={(e) => setReportParams(p => ({ ...p, date_from: e.target.value }))}
                        />
                      </div>

                      <div>
                        <Label className="text-slate-700">To Date</Label>
                        <Input
                          type="date"
                          value={reportParams.date_to}
                          onChange={(e) => setReportParams(p => ({ ...p, date_to: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <Button onClick={generateReport} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                        Generate Report
                      </Button>
                      <Button variant="outline" onClick={exportCSV} disabled={!generatedReport}>
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats Preview */}
                {generatedReport && (
                  <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-slate-900">{generatedReport.report_type}</CardTitle>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={saveReport}>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowReportDialog(true)}>
                          View Full Report
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {renderReportContent()}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Saved Reports Tab */}
          <TabsContent value="saved" className="space-y-4" data-testid="saved-content">
            {savedReports.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Saved Reports</h3>
                  <p className="text-slate-600">Generate and save reports to view them here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {savedReports.map((report) => (
                  <Card key={report.id} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm hover:border-indigo-300 cursor-pointer transition-all" onClick={() => loadSavedReport(report.id)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">{report.report_type}</h4>
                          <p className="text-xs text-slate-500 mt-1">
                            {report.generated_at ? format(parseISO(report.generated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                          </p>
                        </div>
                        <Badge className="bg-indigo-100 text-indigo-700">
                          {report.summary?.total_studies || report.summary?.total_assessments || report.summary?.total_activities || 0}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Scheduled Reports Tab */}
          <TabsContent value="scheduled" className="space-y-6" data-testid="scheduled-content">
            <ScheduledReportsContent />
          </TabsContent>
        </Tabs>
      </main>

      {/* Full Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{generatedReport?.report_type || 'Report'}</DialogTitle>
            <DialogDescription>
              Generated at {generatedReport?.generated_at ? format(parseISO(generatedReport.generated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
            </DialogDescription>
          </DialogHeader>
          {renderReportContent()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Close</Button>
            <Button onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
